import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ExploitationContext } from 'utils/context';
import { emptyCulture, getColorByName, getRandomColor } from 'utils/culturesColors';
import { displayErrorMessage, previousCultureRange } from 'utils/tools_functions';
import { createGroupementParcel, editGroupementParcel } from 'services/API/Exploitation';
import {
  filterParcelFromActivityDate,
  getCultureCampaignKeyFromYear,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';

export const emptyList = [];

export const previousCultureOffsets = previousCultureRange();

export const modes = { cultures: 'cultures', groundTypes: 'groundTypes', groups: 'groups' };

export const requestFieldName = (offset) =>
  offset === 0 ? 'forecast' : `previousCulture${offset}`;

export const requestFieldOffset = (field) =>
  field === 'forecast' ? 0 : Number(field.replace('previousCulture', ''));

const offsetToYear = (forecastYear, offset) => forecastYear - offset;

const yearToOffset = (forecastYear, year) => forecastYear - year;

const assignColors = (names) => {
  const usedColors = [];
  return names.map((name) => {
    const color = getRandomColor(usedColors);
    usedColors.push(`#${color}`);
    return { name, color };
  });
};

const noSoilEntry = { name: 'soil-none', color: 'e3e3e3' };

const noGroupEntry = { name: 'group-none', color: 'e3e3e3' };

const groupEntryName = (id) => `group-${id}`;

const getSavedGroupId = (parcel) => parcel.parent ?? (parcel.isParent ? parcel.id : null);

const addItemForYear = (list, item, year) =>
  list.some((i) => i.id === item.id && i.year === year) ? list : [...list, { ...item, year }];

const applyRequest = (parcel, request, { cultures, groundTypes, forecastYear }) => {
  const parcelCultures = { ...parcel.parcelCultures };
  [0, ...previousCultureOffsets].forEach((offset) => {
    const culture = cultures.find((c) => c.name === request[requestFieldName(offset)]);
    if (culture) {
      parcelCultures[getCultureCampaignKeyFromYear(offsetToYear(forecastYear, offset))] = {
        ...culture,
        surface: parcel.surface,
      };
    }
  });
  const groundType = groundTypes.find((g) => g.name === request.groundType);
  return { ...parcel, parcelCultures, groundType: groundType ?? parcel.groundType };
};

export const useMapParcelsData = ({
  parcels,
  cultures,
  groundTypes,
  forecastCampaign,
  fromProcedure,
  parcelListData,
  stackedParcelsRequestsRef,
  setIsAllowedSubmit,
  hasPendingChanges,
}) => {
  const [pendingVersion, setPendingVersion] = useState(0);
  const forecastYear = forecastCampaign.startYear + 1;

  const parcelsData = useMemo(() => {
    if (fromProcedure) {
      return parcelListData || [];
    }
    const activeParcels = parcels ? filterParcelFromActivityDate(parcels, forecastCampaign) : [];
    const requests = stackedParcelsRequestsRef?.current ?? [];
    if (!hasPendingChanges || !requests.length) return activeParcels;
    return activeParcels.map((parcel) => {
      const request = requests.find((r) => r.id === (parcel.parent ?? parcel.id));
      return request
        ? applyRequest(parcel, request, { cultures, groundTypes, forecastYear })
        : parcel;
    });
  }, [
    fromProcedure,
    parcelListData,
    parcels,
    cultures,
    groundTypes,
    forecastCampaign,
    hasPendingChanges,
    pendingVersion,
  ]);

  const stackRequest = (parcelId, changes) => {
    const requests = stackedParcelsRequestsRef.current;
    const existingRequest = requests.find((request) => request.id === parcelId);
    if (existingRequest) {
      Object.assign(existingRequest, changes);
    } else {
      requests.push({ id: parcelId, ...changes });
    }
    setIsAllowedSubmit(true);
    setPendingVersion((current) => current + 1);
  };

  return { parcelsData, stackRequest };
};

const useItemSelection = (items, stackRequest) => {
  const [selectedId, setSelectedId] = useState(null);
  const [addedItems, setAddedItems] = useState([]);
  const [itemToAdd, setItemToAdd] = useState(null);

  const select = (id, year = null) => {
    setSelectedId(id);
    const item = items.find((i) => i.id === id);
    if (item) setAddedItems((prev) => addItemForYear(prev, item, year));
  };

  const addItem = (year = null) => {
    if (!itemToAdd) return;
    setAddedItems((prev) => addItemForYear(prev, itemToAdd, year));
    setItemToAdd(null);
  };

  const selectedItem = items.find((item) => item.id === selectedId) ?? null;

  const paint = (parcelId, getChanges) => {
    if (!selectedItem) return false;
    stackRequest(parcelId, getChanges(selectedItem));
    return true;
  };

  const lockOpacity = (opacity) => (selectedItem ? 1 : opacity);

  return {
    selectedId,
    selectedItem,
    paint,
    lockOpacity,
    addedItems,
    itemToAdd,
    setItemToAdd,
    select,
    addItem,
  };
};

const useItemsMode = ({ items, stackRequest, getParcelItem, getChanges, palette = null }) => {
  const { culturesColors } = useContext(ExploitationContext);
  const selection = useItemSelection(items, stackRequest);

  const getItemColor = (name) =>
    palette ? palette.byName[name] : getColorByName(name, culturesColors);

  const getCultureForParcel = (parcel, year) => {
    const item = getParcelItem(parcel, year);
    return item?.name ? item : { ...emptyCulture, year };
  };

  const getPolygonStyleParams = (parcel, culture, opacity) => ({
    color: palette
      ? (palette.entries.find((entry) => entry.name === culture?.name) ?? palette.none)
      : Array.isArray(culture)
        ? culture
        : (culture?.name ?? culture),
    opacity: selection.lockOpacity(opacity),
    organicState: parcel?.organicState,
    ...(palette && { culturesColors: palette.entries }),
  });

  const onParcelClick = (parcel, clickedYear) =>
    selection.paint(parcel.id, (item) => getChanges(item, clickedYear));

  return {
    selection,
    colorsByName: palette?.byName ?? null,
    getItemColor,
    getCultureForParcel,
    getPolygonStyleParams,
    onParcelClick,
  };
};

export const useCultureMode = ({ cultures, forecastYear, stackRequest }) =>
  useItemsMode({
    items: cultures,
    stackRequest,
    getParcelItem: (parcel, year) => parcel?.parcelCultures?.[getCultureCampaignKeyFromYear(year)],
    getChanges: (item, clickedYear) => ({
      [requestFieldName(yearToOffset(forecastYear, clickedYear))]: item.name,
    }),
  });

export const useSoilMode = ({ groundTypes, stackRequest }) => {
  const palette = useMemo(() => {
    const colors = assignColors(groundTypes.map((groundType) => groundType.name));
    return {
      entries: [...colors, noSoilEntry],
      none: noSoilEntry,
      byName: Object.fromEntries(colors.map((entry) => [entry.name, entry.color])),
    };
  }, [groundTypes]);

  return useItemsMode({
    items: groundTypes,
    stackRequest,
    palette,
    getParcelItem: (parcel, year) =>
      parcel?.groundType?.name ? { name: parcel.groundType.name, year } : null,
    getChanges: (item) => ({ groundType: item.name }),
  });
};

export const useGroupMode = ({ parcelsData, refreshData }) => {
  const { exploitationContext } = useContext(ExploitationContext);
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEdit = !!draft?.parent?.isParent;

  const groups = parcelsData
    .filter((parcel) => parcel.isParent)
    .map((parent) => ({
      parent,
      children: parcelsData.filter((parcel) => parcel.parent === parent.id),
    }));

  const parcelIds = parcelsData.map((parcel) => parcel.id).join();

  const colorList = useMemo(
    () => [...assignColors(parcelsData.map((parcel) => groupEntryName(parcel.id))), noGroupEntry],
    [parcelIds],
  );

  const getGroupEntry = (id) =>
    colorList.find((entry) => entry.name === groupEntryName(id)) ?? noGroupEntry;

  const getEligibleChildren = (parent) =>
    parcelsData.filter(
      (parcel) =>
        parcel.id !== parent.id &&
        !parcel.isParent &&
        (!parcel.parent || parcel.parent === parent.id) &&
        parcel.organicState === parent.organicState,
    );

  const getParentOptions = () => parcelsData.filter((parcel) => !parcel.parent && !parcel.isParent);

  const reset = () => setDraft(null);

  const getGroupId = (parcel) => {
    const rootId = getSavedGroupId(parcel);
    if (!draft?.parent) return rootId;
    if (parcel.id === draft.parent.id || draft.children.some((child) => child.id === parcel.id)) {
      return draft.parent.id;
    }
    return rootId === draft.parent.id ? null : rootId;
  };

  const getCultureForParcel = (parcel, year) => {
    const groupId = getGroupId(parcel);
    return groupId ? { name: groupEntryName(groupId), year } : { ...emptyCulture, year };
  };

  const getPolygonStyleParams = (parcel, culture, opacity) => {
    const groupId = getGroupId(parcel);
    return {
      color: groupId ? getGroupEntry(groupId) : noGroupEntry,
      opacity,
      organicState: parcel?.organicState,
      culturesColors: colorList,
    };
  };

  const onParcelClick = (parcel, _clickedYear, clickedParcel) => {
    if (!draft?.parent) {
      if (parcel.isParent) {
        setDraft({ parent: parcel, children: parcelsData.filter((p) => p.parent === parcel.id) });
      } else if (draft) {
        setDraft({ parent: parcel, children: [] });
      }
      return true;
    }
    if (getEligibleChildren(draft.parent).some((child) => child.id === clickedParcel.id)) {
      const isChild = draft.children.some((child) => child.id === clickedParcel.id);
      setDraft({
        ...draft,
        children: isChild
          ? draft.children.filter((child) => child.id !== clickedParcel.id)
          : [...draft.children, clickedParcel],
      });
    }
    return true;
  };

  const save = async () => {
    const payload = {
      parent: draft.parent.id,
      children: draft.children.map((child) => child.id),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await editGroupementParcel(exploitationContext.id, payload, payload.parent);
        toast.success(t('success.groupingUpdated'));
      } else {
        await createGroupementParcel(exploitationContext.id, payload);
        toast.success(t('success.groupingCreated'));
      }
      reset();
      await refreshData?.();
    } catch (err) {
      toast.error(displayErrorMessage(err?.response?.data?.code));
    }
    setSaving(false);
  };

  return {
    draft,
    setDraft,
    saving,
    isEdit,
    groups,
    getGroupEntry,
    getEligibleChildren,
    getParentOptions,
    reset,
    save,
    getCultureForParcel,
    getPolygonStyleParams,
    onParcelClick,
  };
};
